import styled from 'styled-components';

import { TextFieldProps } from './TextField.decl';

import { Flex, FormItem, Grid, TextInput, ThemeProvider, getColor } from '@aircall/tractor-v2';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

const TextFieldInputWithEllipsis = styled(TextInput)<
  Omit<TextFieldProps, 'name' | 'validate'> & { hasHelperText: boolean }
>`
  ${({ ellipsis }) => ellipsis && 'text-overflow: ellipsis'};
  ${({ hasHelperText }) => hasHelperText && `border-radius: 0 8px 8px 0;`};
`;

const StyledHelper = styled(Flex)`
  padding: 0 16px;
  border-radius: 8px 0 0 8px;
  border-top: 1px solid ${getColor('input-border-01')};
  border-bottom: 1px solid ${getColor('input-border-01')};
  border-left: 1px solid ${getColor('input-border-01')};
  background-color: ${getColor('background-01')};
  white-space: nowrap;
`;

const StyledGridContainer = styled(Grid)<{ hasHelperText?: boolean }>`
  position: relative;

  ${({ hasHelperText }) => hasHelperText && `grid-template-columns: fit-content(100%) 1fr;`}
`;

export function TextField({
  name,
  label,
  placeholder,
  type,
  disabled,
  validate,
  autoFocus = false,
  ellipsis = false,
  helperText,
  validateInitial,
  clearable,
  defaultValue,
  ...textFieldInputProps
}: TextFieldProps) {
  const { t } = useTranslation();

  const trimWhiteSpaces = (value: string | undefined) => value?.trim();

  return (
    <ThemeProvider>
      <Field
        component='input'
        name={name}
        validate={validate}
        initialValue={defaultValue}
        format={trimWhiteSpaces}
        formatOnBlur
      >
        {({ input, meta }) => {
          const validationErrors = meta.error || meta.submitError;
          const shouldShowError = (meta.touched || validateInitial) && validationErrors;
          return (
            <FormItem
              label={label}
              name={input.name}
              helpText={shouldShowError && t(validationErrors)}
              validationStatus={shouldShowError && 'error'}
              gridAutoRows='max-content'
            >
              <StyledGridContainer hasHelperText={!!helperText}>
                {helperText && <StyledHelper alignItems='center'>{helperText}</StyledHelper>}
                <TextFieldInputWithEllipsis
                  label={label}
                  onBlur={(event) => input.onBlur(event)}
                  id={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  placeholder={placeholder}
                  type={type}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  ellipsis={ellipsis}
                  onClear={clearable && input.value ? () => input.onChange('') : undefined}
                  hasHelperText={!!helperText}
                  {...textFieldInputProps}
                />
              </StyledGridContainer>
            </FormItem>
          );
        }}
      </Field>
    </ThemeProvider>
  );
}
